/**
 * Flow completion and where a returning consumer lands.
 *
 * `completedAt` records that the consumer intentionally finished a planning path (finishing with
 * self-funding, sending an application, continuing without applying, or finishing an insurance-only
 * plan). It marks a milestone and never freezes anything: the funding plan stays live and every answer
 * stays editable. Once finished, the normal way back in (the flow entry, the overview's auto-resume) is
 * Your Plan; anyone else comes back in at the first step, every answer kept, so they see what they
 * chose instead of landing part-way through.
 *
 * Consumers who sent an application before `completedAt` existed count as finished too: a sent
 * application is the one-time fallback, and their first visit stamps `completedAt` (no bulk migration).
 */
import { FIRST_CONSUMER_STEP, type ConsumerStepId } from './steps';

type Applications = readonly ConsumerApplicationSubmission[];

export function hasReachedFlowCompletion(
  plan: ConsumerPlanState | null | undefined,
  applications: Applications = [],
): boolean {
  return !!plan?.completedAt || applications.length > 0;
}

/** Sent an application before completion was recorded: stamp `completedAt` on their first visit. */
export function needsLegacyFlowCompletionStamp(
  plan: ConsumerPlanState | null | undefined,
  applications: Applications,
): boolean {
  return !!plan && !plan.completedAt && applications.length > 0;
}

export function resolveConsumerEntryStep(
  plan: ConsumerPlanState | null | undefined,
  applications: Applications = [],
): ConsumerStepId {
  return hasReachedFlowCompletion(plan, applications) ? 'your-plan' : FIRST_CONSUMER_STEP;
}
